<template>
  <div class="fill-height d-flex flex-column">
    <div class="d-flex mb-4">
      <v-spacer />
      <download-button-with-pop-up
        :title="t('download_form.station_metadata')"
        :testid="'station-metadata'"
        outlined
        :format-choices="[{ title: 'CSV', value: 'csv' }]"
        :file-suffix="' ' + t('download_form.station_metadata')"
        @download="downloadStationMetadata($event)"
      />
      <download-button-with-pop-up
        :title="t('download_form.parameter_metadata')"
        :testid="'station-parameter-metadata'"
        outlined
        :format-choices="[{ title: 'CSV', value: 'csv' }]"
        :file-suffix="' ' + t('download_form.parameter_metadata')"
        @download="downloadParameterMetadata($event)"
      />
      <download-button-with-pop-up
        :title="t('download_form.dataset')"
        :testid="'station-data'"
        :format-choices="[
          { title: 'CSV', value: 'csv' },
          { title: 'GeoJSON', value: 'json' },
        ]"
        :file-suffix="' ' + t('download_form.dataset')"
        :default-start-date="stationStore.filters.startDate"
        :default-end-date="stationStore.filters.endDate"
        :show-date-picker="true"
        :date-picker-text-field-props="{
          'error-messages': dateRangeTextFieldErrorMsg,
        }"
        :disable-download-button="!!dateRangeTextFieldErrorMsg"
        @download="downloadDataset($event)"
        @date-range-changed="generateDateRangeTextFieldErrorMsg"
      />
    </div>
    <div class="d-flex flex-grow-1">
      <v-card class="width-50 mr-2 station-card">
        <v-card-title class="zamg-text-headline">
          {{ stationCardTitle }}
        </v-card-title>
        <v-card-text class="fill-height d-flex flex-column">
          <div class="scrollable-container">
            <simple-station-table
              class="mt-4 station-table"
              :items="stationStore.selectedStations"
              :is-loading="false"
              disable-pagination
              :hidden-header-names="[
                'state',
                'altitude',
                'hasSunshine',
                'hasGlobalRadiation',
                'isActive',
              ]"
              :grey-out-unmatched-substations="false"
              :hide-top-divider="true"
            />
          </div>
        </v-card-text>
      </v-card>
      <parameter-card
        class="station-card ml-2"
        :title="parameterCardTitle"
        :items="parameterStore.selectedParameters"
        :selected="[]"
        :show-select="false"
        :show-search="false"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue';
import DownloadService from '@/service/download-service';
import DatasetStore from '@/stores/dataset/dataset-module';
import ParameterStore from '@/stores/parameter/parameter-module';
import StationStore from '@/stores/station/station-module';
import DownloadButtonWithPopUp from '@/components/common/DownloadButtonWithPopUp.vue';
import ParameterCard from '@/components/common/ParameterCard.vue';
import SimpleStationTable from '@/components/station/SimpleStationTable.vue';
import { areDateRangesOverlapping } from '@/util/datetime';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'StationDownloadPage',
  components: {
    SimpleStationTable,
    DownloadButtonWithPopUp,
    ParameterCard,
  },
  setup() {
    const { t } = useI18n();

    // TODO: generalize cards in this page. There is shared code with StationTable and ParameterCard, Issue #51
    const stationStore = StationStore;
    const datasetStore = DatasetStore;
    const parameterStore = ParameterStore;
    const downloadService = DownloadService;

    const dateRangeTextFieldErrorMsg = ref('');

    const stationCardTitle = computed<string>(() => {
      return `${t('download_form.selected_stations')} (${
        stationStore.selectedStations.length
      } ${t('download_form.to')} ${stationStore.allStations.length})`;
    });

    const parameterCardTitle = computed<string>(() => {
      return `${t('parameter.selected_parameters')} (${
        parameterStore.selectedParameters.length
      } ${t('download_form.to')} ${
        parameterStore.allParameters.length
      })`;
    });

    function generateDateRangeTextFieldErrorMsg(
      dateRange: [Date, Date] | null
    ) {
      if (!dateRange) dateRangeTextFieldErrorMsg.value = '';

      const isAnyStationDateRangeOverlapping = stationStore.selectedStations.some(
        (station) => {
          return areDateRangesOverlapping(
            [station.startDate, station.endDate],
            dateRange as [Date, Date]
          );
        }
      );

      if (!isAnyStationDateRangeOverlapping) {
        dateRangeTextFieldErrorMsg.value = t(
          'download_form.dataset_empty'
        ).toString();
      } else {
        dateRangeTextFieldErrorMsg.value = '';
      }
    }

    const downloadParameterMetadata = async (obj: {
      fileName: string;
      downloadRange: [Date, Date];
    }): Promise<void> => {
      const url = await downloadService.getStationParameterMetadataDownloadURL(
        obj.fileName
      );
      window.open(url, '_blank');
    };

    const downloadStationMetadata = async (obj: {
      fileName: string;
      downloadRange: [Date, Date];
    }): Promise<void> => {
      const url = await downloadService.getStationStationMetadataDownloadURL(
        obj.fileName
      );
      window.open(url, '_blank');
    };

    const downloadDataset = async (obj: {
      fileName: string;
      downloadRange: [Date, Date];
    }): Promise<void> => {
      const url = await downloadService.getStationDatasetDownloadURL(obj);
      window.open(url, '_blank');
    };

    return {
      t,
      stationStore,
      datasetStore,
      parameterStore,
      dateRangeTextFieldErrorMsg,
      stationCardTitle,
      parameterCardTitle,
      generateDateRangeTextFieldErrorMsg,
      downloadParameterMetadata,
      downloadStationMetadata,
      downloadDataset,
    }
  },
});
</script>

<style scoped>
/*TODO: duplicate style from ParamterCard.vue*/
.station-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;
}

.v-card-text {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
}

.scrollable-container {
  /* position: relative; */
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
}
.station-table {
  /* position: absolute; */
  width: 100%;
}
.width-50 {
  width: 50%;
}
</style>
